import { useSpace } from '@/store/modules/App/space'

/**
 * @function filterByPermissions
 * @description
 * Filters an array of `Component` or `ComponentGroup` items based on user permissions.
 * It checks allowed and blocked permission keys to determine which items should be included in the result.
 * Optionally, a cross-check permission key can be provided to enforce stricter filtering.
 *
 * @param items - The array of items (`Component[]` or `ComponentGroup[]`) to filter.
 * @param allowPermissionKey - The permission key that specifies which items are allowed.
 * @param blockPermissionKey - The permission key that specifies which items are blocked.
 * @param crossCheckPermissionKey - (Optional) An additional permission key for cross-checking permissions.
 * @returns The filtered array of items, based on the user's permissions.
 */
export function filterByPermissions<T extends { id: number }>(
  items: T[],
  allowPermissionKey: 'managed_component_ids' | 'managed_component_group_uuids',
  blockPermissionKey:
    | 'blocked_manage_component_ids'
    | 'blocked_manage_component_group_uuids',
  crossCheckPermissionKey?:
    | 'managed_component_ids'
    | 'managed_component_group_uuids',
): T[] {
  const spaceStore = useSpace()
  const permissions = spaceStore.userPermissions

  const allowedIds = permissions[allowPermissionKey] || []
  const blockedIds = permissions[blockPermissionKey] || []

  if (crossCheckPermissionKey) {
    const crossCheckIds = permissions[crossCheckPermissionKey] || []
    // If the cross-check permission has values and current permission doesn't, return empty
    if (
      crossCheckIds.length > 0 &&
      allowedIds.length === 0 &&
      blockedIds.length === 0
    ) {
      return []
    }
  }

  function getItemIdentifier(item: T): string | number {
    if ('uuid' in item && typeof item.uuid === 'string') {
      return item.uuid
    }
    return item.id
  }

  if (allowedIds.length) {
    return items.filter((item) => {
      const identifier = getItemIdentifier(item)
      return allowedIds.some(
        (allowedId) => String(allowedId) === String(identifier),
      )
    })
  }

  if (blockedIds.length) {
    return items.filter((item) => {
      const identifier = getItemIdentifier(item)
      return !blockedIds.some(
        (blockedId) => String(blockedId) === String(identifier),
      )
    })
  }

  return items || []
}
